import { Link } from "react-router-dom";
import {
  Settings01Icon as Settings,
  CustomerSupportIcon as LifeBuoy,
  Logout03Icon as LogOut,
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { getAccountMenuConfig } from "./accountMenuNavGroups";
import type { AppRole } from "@/types/supabase";

interface AccountMenuBodyProps {
  variant: "dark" | "light";
  displayName: string;
  email?: string | null;
  role: AppRole | null | undefined;
  planLabel?: string;
  // Verified artists/sponsors have no "plan" to upsell, so the plan badge
  // only makes sense while they're still on the free, unverified tier.
  isVerified?: boolean;
  unreadNotifications?: number;
  showHeader?: boolean;
  onNavigate: () => void;
  onSignOut: () => void;
}

export function AccountMenuBody({
  variant,
  displayName,
  email,
  role,
  planLabel = "Free plan",
  isVerified = false,
  unreadNotifications = 0,
  showHeader = true,
  onNavigate,
  onSignOut,
}: AccountMenuBodyProps) {
  const { dashboardPath, groups } = getAccountMenuConfig(
    role,
    unreadNotifications,
  );
  const dark = variant === "dark";

  const headerBg = dark ? "" : "bg-[#F7F7FB]";
  const headerBorder = dark
    ? "border-white/[0.09]"
    : "border-[#1E1B4B]/10";
  const nameColor = dark ? "text-white" : "text-[#1E1B4B]";
  const emailColor = dark ? "text-[#9A96C4]" : "text-[#6E6B8A]";
  const roleBadge = dark
    ? "text-[#C9C5E8] bg-white/10"
    : "text-[#5A31D8] bg-[#5A31D8]/10";
  const planBadge = dark
    ? "text-[#C9C5E8] bg-white/10"
    : "text-[#6E6B8A] bg-[#EFEFF7]";
  const groupLabelColor = dark ? "text-[#9A96C4]" : "text-[#8B88A3]";
  const linkColor = dark ? "text-[#E6E3F7]" : "text-[#1E1B4B]";
  const linkIconColor = dark ? "#9A96C4" : "#6E6B8A";
  const linkHoverBg = dark ? "hover:bg-white/[0.07]" : "hover:bg-[#F7F7FB]";
  const dividerColor = dark ? "bg-white/[0.09]" : "bg-[#1E1B4B]/10";
  const signOutColor = dark ? "text-[#F87171]" : "text-[#DC2626]";
  const signOutHoverBg = dark
    ? "hover:bg-[#F87171]/[0.12]"
    : "hover:bg-[#DC2626]/[0.07]";

  return (
    <div className="font-archivo">
      {showHeader && (
        <div className={`px-4 pt-[15px] pb-[14px] border-b ${headerBorder} ${headerBg}`}>
          <div className={`font-semibold text-sm leading-tight ${nameColor}`}>
            {displayName}
          </div>
          {email && (
            <div
              className={`mt-1 text-xs leading-tight truncate ${emailColor}`}
            >
              {email}
            </div>
          )}
          <div className="mt-2.5 flex gap-1.5">
            <span
              className={`font-jetbrains font-semibold text-[10px] px-2 py-1.5 rounded-[2px] uppercase ${roleBadge}`}
            >
              {role || "Guest"}
            </span>
            {!isVerified && (
              <span
                className={`font-jetbrains font-semibold text-[10px] px-2 py-1.5 rounded-[2px] uppercase ${planBadge}`}
              >
                {planLabel}
              </span>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col py-1">
        {groups.map((group) => (
          <div key={group.label}>
            <span
              className={`block px-4 pt-3 pb-1.5 font-jetbrains font-bold text-[10px] tracking-[0.16em] uppercase ${groupLabelColor}`}
            >
              {group.label}
            </span>
            {group.items.map((item) => {
              return (
                <Link
                  key={item.tab}
                  to={
                    item.tab
                      ? `${dashboardPath}?tab=${item.tab}`
                      : dashboardPath
                  }
                  onClick={onNavigate}
                  className={`flex items-center justify-between gap-2.5 h-10 px-4 text-[13px] font-medium ${linkColor} ${linkHoverBg}`}
                >
                  <span className="flex items-center gap-[11px]">
                    <HugeiconsIcon
                      icon={item.icon}
                      size={16}
                      className="flex-shrink-0"
                      color={linkIconColor}
                      strokeWidth={1.5}
                    />
                    {item.label}
                  </span>
                  {!!item.badgeCount && (
                    <span className="font-jetbrains font-bold text-[10px] text-white bg-[#5A31D8] px-[7px] py-[5px] rounded-[2px]">
                      {item.badgeCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </div>

      <div className={`h-px ${dividerColor}`} />

      <div className="flex flex-col py-1.5">
        <Link
          to="/profile"
          onClick={onNavigate}
          className={`flex items-center gap-[11px] h-10 px-4 text-[13px] font-medium ${linkColor} ${linkHoverBg}`}
        >
          <HugeiconsIcon icon={Settings} size={16} color={linkIconColor} strokeWidth={1.5} />
          Edit Profile
        </Link>
        <Link
          to="/my-support-tickets"
          onClick={onNavigate}
          className={`flex items-center gap-[11px] h-10 px-4 text-[13px] font-medium ${linkColor} ${linkHoverBg}`}
        >
          <HugeiconsIcon icon={LifeBuoy} size={16} color={linkIconColor} strokeWidth={1.5} />
          Support
        </Link>
      </div>

      <div className={`h-px ${dividerColor}`} />

      <button
        type="button"
        onClick={() => {
          onNavigate();
          onSignOut();
        }}
        className={`flex items-center gap-[11px] w-full h-11 px-4 my-1 font-semibold text-[13px] text-left ${signOutColor} ${signOutHoverBg}`}
      >
        <HugeiconsIcon icon={LogOut} size={16} strokeWidth={1.5} />
        Sign Out
      </button>
    </div>
  );
}
